import { useCallback } from 'react';
import { toast } from '../../ui/simple-toaster';
import { api } from '../../../utils/api';

export const useVehicleHandlers = ({
  driverInfo,
  vehicles,
  setVehicles,
  otherVehicleForm,
  setVehicleLoading,
  setShowOtherVehicleForm,
  setShowVehicleModal,
  setShowRadiusSelectionModal,
  setPendingVehicleSelection,
  pendingVehicleSelection,
  setSelectedVehicle,
  setRadiusSelectionLoading,
  setPendingTrailers,
  loadDriverData,
}) => {
  const loadVehicles = useCallback(async () => {
    try {
      const res = await api('/vehicles');
      setVehicles(res.vehicles || []);
    } catch (err) { console.error('Failed to load vehicles:', err); }
  }, [setVehicles]);

  const handleOtherVehicleSubmit = useCallback(async (e) => {
    e.preventDefault();
    setVehicleLoading(true);
    try {
      const result = await api('/vehicles', {
        method: 'POST',
        body: JSON.stringify(otherVehicleForm)
      });
      toast.success('Vehicle added');
      setShowOtherVehicleForm(false);
      loadVehicles();

      if (result.vehicle) {
        const newVehicle = result.vehicle;
        if (newVehicle.gvm_kg >= 4500 && newVehicle.gvm_kg < 12000) {
          setPendingVehicleSelection({ vehicle: newVehicle, isNew: true });
          setShowVehicleModal(false);
          setShowRadiusSelectionModal(true);
        } else {
          setSelectedVehicle(newVehicle);
          localStorage.setItem('lawd_selected_vehicle', JSON.stringify(newVehicle));
          await api(`/drivers/${driverInfo.driver_id}/select-vehicle`, {
            method: 'POST',
            body: JSON.stringify({ vehicle_id: newVehicle.vehicle_id })
          });
          setShowVehicleModal(false);
          loadDriverData();
        }
      }
    } catch (err) { toast.error('Failed to add vehicle: ' + err.message); }
    finally { setVehicleLoading(false); }
  }, [driverInfo, otherVehicleForm, setVehicleLoading, setShowOtherVehicleForm, setShowVehicleModal, setShowRadiusSelectionModal, setPendingVehicleSelection, setSelectedVehicle, loadVehicles, loadDriverData]);

  const handleVehicleSelect = useCallback(async (vehicleId, vehicle, trailerIds = []) => {
    setVehicleLoading(true);
    setPendingTrailers(trailerIds);
    try {
      const selectedV = vehicle || vehicles.find(v => v.vehicle_id === vehicleId);
      if (selectedV && selectedV.gvm_kg >= 4500 && selectedV.gvm_kg < 12000) {
        setPendingVehicleSelection({ vehicle: selectedV, isNew: false });
        setShowVehicleModal(false);
        setShowRadiusSelectionModal(true);
        setVehicleLoading(false);
        return;
      }

      setSelectedVehicle(selectedV);
      localStorage.setItem('lawd_selected_vehicle', JSON.stringify(selectedV));

      await api(`/drivers/${driverInfo.driver_id}/select-vehicle`, {
        method: 'POST',
        body: JSON.stringify({ vehicle_id: vehicleId })
      });

      setShowVehicleModal(false);
      loadDriverData();
    } catch (err) { toast.error('Failed to select vehicle: ' + err.message); }
    finally { setVehicleLoading(false); }
  }, [driverInfo, vehicles, setVehicleLoading, setPendingTrailers, setPendingVehicleSelection, setShowVehicleModal, setShowRadiusSelectionModal, setSelectedVehicle, loadDriverData]);

  const handleRadiusSelection = useCallback(async (radiusKm) => {
    if (!pendingVehicleSelection) return;
    setRadiusSelectionLoading(true);
    try {
      const vehicle = pendingVehicleSelection.vehicle;

      await api(`/vehicles/${vehicle.vehicle_id}/radius`, {
        method: 'PUT',
        body: JSON.stringify({ primary_production_radius_km: radiusKm })
      });

      const updatedVehicle = { ...vehicle, primary_production_radius_km: radiusKm };
      setSelectedVehicle(updatedVehicle);
      localStorage.setItem('lawd_selected_vehicle', JSON.stringify(updatedVehicle));

      await api(`/drivers/${driverInfo.driver_id}/select-vehicle`, {
        method: 'POST',
        body: JSON.stringify({ vehicle_id: vehicle.vehicle_id })
      });

      setShowRadiusSelectionModal(false);
      setPendingVehicleSelection(null);
      loadDriverData();
      toast.success(`Vehicle selected with ${radiusKm}km radius`);
    } catch (err) { toast.error('Failed to set radius: ' + err.message); }
    finally { setRadiusSelectionLoading(false); }
  }, [driverInfo, pendingVehicleSelection, setRadiusSelectionLoading, setSelectedVehicle, setShowRadiusSelectionModal, setPendingVehicleSelection, loadDriverData]);

  const cancelRadiusSelection = useCallback(() => {
    setShowRadiusSelectionModal(false);
    setPendingVehicleSelection(null);
    setShowVehicleModal(true);
  }, [setShowRadiusSelectionModal, setPendingVehicleSelection, setShowVehicleModal]);

  return {
    loadVehicles,
    handleOtherVehicleSubmit,
    handleVehicleSelect,
    handleRadiusSelection,
    cancelRadiusSelection,
  };
};
